import React, { useState, useMemo } from 'react';
import { useCentroOperaciones } from '../../hooks/useCentroOperaciones';
import TablaCostosSL from './TablaCostosSL';
import FormularioProducto from './FormularioProducto';
import { ClipLoader } from 'react-spinners';
import { FaChartBar, FaTag, FaWallet } from 'react-icons/fa';

// 🔥 PANEL FINANCIERO KOKIMIKO 🔥
export default function CentroOperaciones() {
    // Usamos el hook original pero adaptado a la lógica de eventos
    const { productos, cargando, eliminarProducto, agregarProducto, editarProducto } = useCentroOperaciones();
    const [activeTab, setActiveTab] = useState('catalogo');

    // Mantenemos un margen objetivo global para el cálculo de rentabilidad sugerida
    const [margenObjetivo, setMargenObjetivo] = useState(25);

    // Cálculos rápidos del catálogo
    const resumenCatalogo = useMemo(() => {
        if (!productos || productos.length === 0) return { totalServicios: 0, promedioCosto: 0, promedioPVP: 0 };
        
        let totalCostos = 0;
        let totalPVP = 0;

        productos.forEach(p => {
            const costo = parseFloat(p.materiales) || parseFloat(p.costoBase) || 0; // Adaptado para leer costo base
            const pvp = parseFloat(p.precioSugerido) || parseFloat(p.costo) || 0; // Adaptado para leer PVP
            totalCostos += costo;
            totalPVP += pvp;
        });

        return {
            totalServicios: productos.length,
            promedioCosto: totalCostos / productos.length,
            promedioPVP: totalPVP / productos.length
        };
    }, [productos]);

    const formatCurrency = (value) => Number(value).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

    if (cargando) return <div className="flex justify-center items-center h-full min-h-[500px]"><ClipLoader color="#1BB1A5" size={50} /></div>;

    const tabButtonStyle = "px-6 py-3 font-black text-[10px] uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 outline-none rounded-t-2xl";
    const activeStyle = "border-kokimiko-teal text-kokimiko-texto bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]";
    const inactiveStyle = "border-transparent text-gray-400 bg-gray-100 hover:bg-gray-200";

    return (
        <div className="p-4 md:p-8 bg-kokimiko-fondo/30 h-full overflow-y-auto font-sans">
            
            {/* CABECERA KOKIMIKO */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-[2rem] shadow-sm mb-6 border border-gray-100 gap-4">
                <div>
                    <h2 className="text-2xl md:text-3xl font-black text-kokimiko-texto uppercase tracking-widest flex items-center gap-3">
                        <div className="p-3 bg-kokimiko-teal rounded-xl text-white"><FaWallet /></div>
                        Finanzas y Catálogo
                    </h2>
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-2 ml-14">Gestión de Rentabilidad de Eventos</p>
                </div>
            </div>

            {/* DASHBOARD RÁPIDO */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-4">
                    <div className="p-4 bg-kokimiko-teal/10 rounded-2xl text-kokimiko-teal">
                        <FaTag size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Servicios en Catálogo</p>
                        <p className="text-3xl font-black text-kokimiko-texto">{resumenCatalogo.totalServicios}</p>
                    </div>
                </div>
                
                <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-4">
                    <div className="p-4 bg-kokimiko-naranja/10 rounded-2xl text-kokimiko-naranja">
                        <FaChartBar size={24} />
                    </div>
                    <div className="flex-1">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Margen Objetivo Sugerido</p>
                        <div className="flex items-center gap-2 mt-1">
                            <input 
                                type="number" 
                                value={margenObjetivo} 
                                onChange={(e) => setMargenObjetivo(e.target.value)}
                                className="w-16 p-1 text-xl font-black text-kokimiko-naranja bg-transparent border-b-2 border-kokimiko-naranja/30 focus:border-kokimiko-naranja outline-none text-center"
                            />
                            <span className="text-xl font-black text-kokimiko-naranja">%</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-4">
                    <div className="p-4 bg-kokimiko-verde/10 rounded-2xl text-kokimiko-verde">
                        <FaWallet size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">PVP Promedio Catálogo</p>
                        <p className="text-3xl font-black text-kokimiko-verde">{formatCurrency(resumenCatalogo.promedioPVP)}</p>
                    </div>
                </div>
            </div>

            {/* SELECTOR DE PESTAÑAS */}
            <div className="flex border-b border-gray-200">
                <button 
                    onClick={() => setActiveTab('catalogo')} 
                    className={`${tabButtonStyle} ${activeTab === 'catalogo' ? activeStyle : inactiveStyle}`}
                >
                    <FaTag size={14}/> Catálogo de Servicios y Precios
                </button>
            </div>

            {/* CONTENIDO DEL CATÁLOGO */}
            <div className="bg-white p-6 rounded-b-[2rem] rounded-tr-[2rem] border border-gray-100 shadow-sm min-h-[400px]">
                {activeTab === 'catalogo' && (
                    <div className="animate-fade-in flex flex-col gap-6">
                        
                        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                            <h3 className="text-xs font-black text-kokimiko-texto uppercase tracking-widest mb-4">Añadir Nuevo Servicio al Catálogo</h3>
                            {/* Pasamos el margen al formulario si está preparado para recibirlo, sino, el hook lo maneja */}
                            <FormularioProducto agregarProducto={agregarProducto} margenDefecto={margenObjetivo} />
                        </div>
                        
                        <div className="mt-4">
                            <h3 className="text-xs font-black text-kokimiko-texto uppercase tracking-widest mb-4 border-b pb-2">Base de Datos Maestra</h3>
                            <TablaCostosSL 
                                productos={productos} 
                                editarProducto={editarProducto} 
                                eliminarProducto={eliminarProducto} 
                                margenObjetivo={margenObjetivo}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}